import { normalizeImportHeaderLabel } from './document-product-xlsx-import.helper';

/** Première colonne dont l’en-tête correspond à l’un des alias (ordre des colonnes Excel). */
function resolvePriceColumnIndex(headerUpper: string[], ...aliases: string[]): number {
  const normalizedAliases = aliases
    .map((a) => normalizeImportHeaderLabel(a))
    .filter((a) => a.length > 0);
  for (let i = 0; i < headerUpper.length; i++) {
    const cellH = headerUpper[i];
    if (cellH && normalizedAliases.includes(cellH)) {
      return i;
    }
  }
  return -1;
}

export function parseErpImportPriceCell(value: unknown): number | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const raw = String(value)
    .trim()
    .replace(/\u00a0/g, ' ')
    .replace(/\s/g, '')
    .replace(/[^\d,.\-]/g, '');
  if (raw === '' || raw === '-' || raw === '.' || raw === ',') {
    return null;
  }
  let normalized = raw;
  if (normalized.includes(',') && normalized.includes('.')) {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  } else {
    normalized = normalized.replace(',', '.');
  }
  const n = parseFloat(normalized);
  return Number.isNaN(n) ? null : n;
}

function pickColumnIndex(...candidates: number[]): number {
  for (const idx of candidates) {
    if (idx != null && idx >= 0) {
      return idx;
    }
  }
  return -1;
}

export function buildErpProductImportPriceHeaderMap(headerRow: unknown[]): Record<string, number> {
  const headerUpper = (headerRow ?? []).map((h) => normalizeImportHeaderLabel(h));
  const idxVenteTtc = resolvePriceColumnIndex(headerUpper, 'VENTE TTC', 'VENTE_TTC', 'PRIX VENTE TTC');
  const idxVenteHt = resolvePriceColumnIndex(
    headerUpper,
    'VENTE HT',
    'VENTE_HT',
    'PRIX VENTE HT',
    'PRIX_VENTE HT'
  );
  const idxAchatHt = resolvePriceColumnIndex(
    headerUpper,
    'ACHAT HT',
    'ACHAT_HT',
    'PRIX ACHAT HT',
    'PRIX ACHAT',
    'PRIX_ACHAT',
    'PA HT'
  );
  const idxAchatTtc = resolvePriceColumnIndex(
    headerUpper,
    'ACHAT TTC',
    'ACHAT_TTC',
    'PRIX ACHAT TTC'
  );
  const idxPrixLegacy = resolvePriceColumnIndex(
    headerUpper,
    'PRIX VENTE',
    'PRIX DE VENTE',
    'PRIX_VENTE',
    'PRIX UNITAIRE',
    'PRIX',
    'PRIX_UNITAIRE'
  );
  return {
    achat_ht: idxAchatHt,
    achat_ttc: idxAchatTtc,
    vente_ht: idxVenteHt,
    /** Uniquement la colonne « vente ttc » — pas de repli sur prix legacy. */
    vente_ttc: idxVenteTtc,
    prix: pickColumnIndex(idxPrixLegacy)
  };
}

export function mergeErpProductImportPriceHeaders(
  headerMap: Record<string, number>,
  headerRow: unknown[]
): Record<string, number> {
  const price = buildErpProductImportPriceHeaderMap(headerRow);
  return {
    ...headerMap,
    achat_ht: price.achat_ht,
    achat_ttc: price.achat_ttc,
    vente_ht: price.vente_ht,
    vente_ttc: price.vente_ttc,
    prix: headerMap.prix != null && headerMap.prix >= 0 ? headerMap.prix : price.prix
  };
}

export function readErpImportRowNumber(
  row: unknown[],
  headerMap: Record<string, number>,
  key: string
): number | null {
  const idx = headerMap[key];
  if (idx == null || idx < 0) {
    return null;
  }
  return parseErpImportPriceCell(row[idx]);
}

export function hasErpProductCatalogPriceValues(prices: ErpProductCatalogPricesFromRow): boolean {
  return (
    (prices.achat_ht != null && prices.achat_ht > 0) ||
    (prices.achat_ttc != null && prices.achat_ttc > 0) ||
    (prices.vente_ht != null && prices.vente_ht > 0) ||
    (prices.vente_ttc != null && prices.vente_ttc > 0)
  );
}

/** Complète les prix variante avec ceux de la ligne parent caisse (souvent seule source des prix). */
export function mergeErpProductCatalogPricesWithParentFallback(
  variant: ErpProductCatalogPricesFromRow,
  parent: ErpProductCatalogPricesFromRow | null | undefined
): ErpProductCatalogPricesFromRow {
  if (!parent || hasErpProductCatalogPriceValues(variant)) {
    return variant;
  }
  const achat_ht = variant.achat_ht ?? parent.achat_ht;
  const achat_ttc = variant.achat_ttc ?? parent.achat_ttc;
  const vente_ht = variant.vente_ht ?? parent.vente_ht;
  const vente_ttc = variant.vente_ttc ?? parent.vente_ttc;
  let prix_unitaires = variant.prix_unitaires;
  if (!(prix_unitaires > 0)) {
    if (vente_ht != null && vente_ht > 0) {
      prix_unitaires = vente_ht;
    } else if (vente_ttc != null && vente_ttc > 0) {
      prix_unitaires = vente_ttc;
    } else {
      prix_unitaires = parent.prix_unitaires;
    }
  }
  return { achat_ht, achat_ttc, vente_ht, vente_ttc, prix_unitaires };
}

export interface ErpProductCatalogPricesFromRow {
  achat_ht: number | null;
  achat_ttc: number | null;
  vente_ht: number | null;
  vente_ttc: number | null;
  prix_unitaires: number;
}

/** Une colonne Excel → un attribut products (pas de mélange entre colonnes). */
export function extractErpProductCatalogPricesFromImportRow(
  row: unknown[],
  headerMap: Record<string, number>
): ErpProductCatalogPricesFromRow {
  const achat_ht = readErpImportRowNumber(row, headerMap, 'achat_ht');
  const achat_ttc = readErpImportRowNumber(row, headerMap, 'achat_ttc');
  const vente_ht = readErpImportRowNumber(row, headerMap, 'vente_ht');
  const vente_ttc = readErpImportRowNumber(row, headerMap, 'vente_ttc');
  const prixLegacy = readErpImportRowNumber(row, headerMap, 'prix');

  let prix_unitaires = 0;
  if (vente_ht != null && vente_ht > 0) {
    prix_unitaires = vente_ht;
  } else if (vente_ttc != null && vente_ttc > 0) {
    prix_unitaires = vente_ttc;
  } else if (prixLegacy != null && prixLegacy > 0) {
    prix_unitaires = prixLegacy;
  }

  return { achat_ht, achat_ttc, vente_ht, vente_ttc, prix_unitaires };
}

/** Payload ERP : achat_ht, achat_ttc, vente_ht, vente_ttc tels que lus dans le fichier. */
export function erpProductCatalogPriceFieldsFromImport(
  prices: ErpProductCatalogPricesFromRow
): Record<string, number> {
  const out: Record<string, number> = {};
  if (prices.achat_ht != null) {
    out.achat_ht = Number(prices.achat_ht);
  }
  if (prices.achat_ttc != null) {
    out.achat_ttc = Number(prices.achat_ttc);
  }
  let venteHt = prices.vente_ht;
  if (venteHt == null && prices.prix_unitaires > 0) {
    venteHt = prices.prix_unitaires;
  }
  if (venteHt != null) {
    out.vente_ht = Number(venteHt);
  }
  if (prices.vente_ttc != null) {
    out.vente_ttc = Number(prices.vente_ttc);
  }
  return out;
}

/**
 * Applique les 4 prix catalogue sur le payload POST/PUT/bulk.
 * La vente HT Excel ne doit pas rester uniquement dans prix_unitaires.
 */
export function applyErpProductCatalogPricesToImportPayload(
  product: Record<string, unknown>,
  prices: ErpProductCatalogPricesFromRow
): void {
  const fields = erpProductCatalogPriceFieldsFromImport(prices);
  Object.assign(product, fields);
  delete product.prix_unitaires;
}
